import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import SEOHead, { seoConfig } from '../components/SEOHead';
import RouteScheme from '../components/RouteScheme';

const ContactPage = () => {
  const { language } = useLanguage();
  const { isEditMode } = useAdmin();
  const [selectedCity, setSelectedCity] = useState('all');
  const seo = seoConfig.contact[language] || seoConfig.contact.en || seoConfig.contact.ru;

  const content = {
    ru: {
      header: {
        title: 'Контакты',
        subtitle: 'Свяжитесь с нами любым удобным способом'
      },
      whatsapp: {
        title: 'Написать в WhatsApp',
        subtitle: 'Быстрый ответ в мессенджере',
        button: 'Открыть WhatsApp'
      },
      telegram: {
        title: 'Telegram',
        subtitle: 'Напишите нам в Telegram',
        button: 'Открыть Telegram'
      },
      contactInfo: {
        title: 'Контактная информация',
        phone2: {
          label: 'Дополнительный',
          value: '+7 747 275 5088',
          raw: '+77472755088'
        },
        phone3: {
          label: 'Дополнительный',
          value: '+7 771 700 4430',
          raw: '+77717004430'
        },
        email: {
          label: 'Написать email',
          value: 'sales@gipsovinil.kz'
        },
        ceoEmail: {
          label: 'Почта руководителя',
          value: 'ceo@gipsovinil.kz'
        },
        address: {
          label: 'Адрес производства',
          value: 'СЭЗ Астана, Казахстан'
        },
        hours: {
          label: 'Режим работы',
          value: 'Пн-Пт: 9:00 - 18:00'
        }
      },
      dealers: {
        title: 'Наши дилеры',
        subtitle: 'Найдите ближайшего официального дилера',
        all: 'Все города',
        showOnMap: 'На карте',
        contactDealer: 'Связаться'
      }
    },
    kz: {
      header: {
        title: 'Байланыс',
        subtitle: 'Бізбен кез келген ыңғайлы жолмен байланысыңыз'
      },
      whatsapp: {
        title: 'WhatsApp-қа жазу',
        subtitle: 'Мессенджерде жылдам жауап',
        button: 'WhatsApp ашу'
      },
      telegram: {
        title: 'Telegram',
        subtitle: 'Telegram-ға жазыңыз',
        button: 'Telegram ашу'
      },
      contactInfo: {
        title: 'Байланыс ақпараты',
        phone2: {
          label: 'Қосымша',
          value: '+7 747 275 5088',
          raw: '+77472755088'
        },
        phone3: {
          label: 'Қосымша',
          value: '+7 771 700 4430',
          raw: '+77717004430'
        },
        email: {
          label: 'Email жазу',
          value: 'sales@gipsovinil.kz'
        },
        ceoEmail: {
          label: 'Басшы поштасы',
          value: 'ceo@gipsovinil.kz'
        },
        address: {
          label: 'Өндіріс мекенжайы',
          value: 'АЭА Астана, Қазақстан'
        },
        hours: {
          label: 'Жұмыс режимі',
          value: 'Дс-Жм: 9:00 - 18:00'
        }
      },
      dealers: {
        title: 'Біздің дилерлер',
        subtitle: 'Жақын ресми дилерді табыңыз',
        all: 'Барлық қалалар',
        showOnMap: 'Картада',
        contactDealer: 'Байланысу'
      }
    },
    en: {
      header: {
        title: 'Contact',
        subtitle: 'Get in touch with us in any convenient way'
      },
      whatsapp: {
        title: 'Write on WhatsApp',
        subtitle: 'Quick reply in messenger',
        button: 'Open WhatsApp'
      },
      telegram: {
        title: 'Telegram',
        subtitle: 'Write to us on Telegram',
        button: 'Open Telegram'
      },
      contactInfo: {
        title: 'Contact Information',
        phone2: {
          label: 'Additional',
          value: '+7 747 275 5088',
          raw: '+77472755088'
        },
        phone3: {
          label: 'Additional',
          value: '+7 771 700 4430',
          raw: '+77717004430'
        },
        email: {
          label: 'Write email',
          value: 'sales@gipsovinil.kz'
        },
        ceoEmail: {
          label: 'CEO email',
          value: 'ceo@gipsovinil.kz'
        },
        address: {
          label: 'Production address',
          value: 'SEZ Astana, Kazakhstan'
        },
        hours: {
          label: 'Working hours',
          value: 'Mon-Fri: 9:00 - 18:00'
        }
      },
      dealers: {
        title: 'Our Dealers',
        subtitle: 'Find the nearest official dealer',
        all: 'All cities',
        showOnMap: 'On map',
        contactDealer: 'Contact'
      }
    },
    ar: {
      header: {
        title: 'تواصل معنا',
        subtitle: 'تواصل معنا بأي طريقة مناسبة لك'
      },
      whatsapp: {
        title: 'راسلنا عبر واتساب',
        subtitle: 'رد سريع عبر تطبيق المراسلة',
        button: 'فتح واتساب'
      },
      telegram: {
        title: 'تيليغرام',
        subtitle: 'راسلنا عبر تيليغرام',
        button: 'فتح تيليغرام'
      },
      contactInfo: {
        title: 'معلومات الاتصال',
        phone2: {
          label: 'رقم إضافي',
          value: '+7 747 275 5088',
          raw: '+77472755088'
        },
        phone3: {
          label: 'رقم إضافي',
          value: '+7 771 700 4430',
          raw: '+77717004430'
        },
        email: {
          label: 'راسلنا عبر البريد الإلكتروني',
          value: 'sales@gipsovinil.kz'
        },
        ceoEmail: {
          label: 'البريد الإلكتروني للرئيس التنفيذي',
          value: 'ceo@gipsovinil.kz'
        },
        address: {
          label: 'عنوان المصنع',
          value: 'المنطقة الاقتصادية الخاصة أستانا، كازاخستان'
        },
        hours: {
          label: 'ساعات العمل',
          value: 'الاثنين - الجمعة: 9:00 - 18:00'
        }
      },
      dealers: {
        title: 'موزعونا المعتمدون',
        subtitle: 'ابحث عن أقرب موزع رسمي معتمد',
        all: 'جميع المدن',
        showOnMap: 'على الخريطة',
        contactDealer: 'تواصل'
      }
    }
  };

  // Real dealers data from PDF
  const dealers = [
    {
      id: 1,
      name: { ru: 'ТОО "Profil House Almaty"', kz: 'ЖШС "Profil House Almaty"', en: 'Profil House Almaty LLP', ar: 'شركة بروفيل هاوس ألماتي ذ.م.م' },
      city: 'almaty',
      cityName: { ru: 'Алматы', kz: 'Алматы', en: 'Almaty', ar: 'ألماتي' },
      address: { 
        ru: 'г. Алматы, ул. 22-я линия, дом 45, офис 208', 
        kz: 'Алматы қ., 22-ші желі көшесі, 45 үй, 208 кеңсе', 
        en: 'Almaty, 22nd line str., 45, office 208', ar: 'ألماتي، شارع الخط 22، 45، مكتب 208' 
      },
      phones: ['+7 777 366 2033'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/almaty/search/%D0%90%D0%BB%D0%BC%D0%B0%D1%82%D1%8B%2C%2022%20%D0%BB%D0%B8%D0%BD%D0%B8%D1%8F%2045'
    },
    {
      id: 2,
      name: { ru: 'ТОО "ST-KZ PROFI"', kz: 'ЖШС "ST-KZ PROFI"', en: 'ST-KZ PROFI LLP', ar: 'شركة إس تي - كا زد بروفي ذ.م.م' },
      city: 'astana',
      cityName: { ru: 'Астана', kz: 'Астана', en: 'Astana', ar: 'أستانا' },
      address: { 
        ru: 'г. Астана, Кабанбай батыр проспект, 63', 
        kz: 'Астана қ., Қабанбай батыр даңғылы, 63', 
        en: 'Astana, Kabanbay batyr ave., 63', ar: 'أستانا، شارع كابانباي باتير، 63' 
      },
      phones: ['+7 708 103 3026'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/astana/geo/70030076517463435/71.395683,51.073891'
    },
    {
      id: 3,
      name: { ru: 'ИП Жагупарова Н.', kz: 'ЖК Жағупарова Н.', en: 'IE Zhaguparova N.', ar: 'مؤسسة فردية جاغوباروفا ن.' },
      city: 'astana',
      cityName: { ru: 'Астана', kz: 'Астана', en: 'Astana', ar: 'أستانا' },
      address: { 
        ru: 'г. Астана, пр. Богенбай батыра, 6/5, рынок "Эталон", 2 этаж', 
        kz: 'Астана қ., Бөгенбай батыр даңғылы, 6/5, "Эталон" базары, 2 қабат', 
        en: 'Astana, Bogenbay batyr ave., 6/5, Etalon market, 2nd floor', ar: 'أستانا، شارع بوغينباي باتير، 6/5، سوق إيتالون، الطابق الثاني' 
      },
      phones: ['+7 701 520 4749'],
      workHours: { ru: 'Пн-Сб: 9:00-18:00', kz: 'Дс-Сб: 9:00-18:00', en: 'Mon-Sat: 9:00-18:00', ar: 'الاثنين - السبت: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/astana/search/%D0%90%D1%81%D1%82%D0%B0%D0%BD%D0%B0%2C%20%D0%91%D0%BE%D0%B3%D0%B5%D0%BD%D0%B1%D0%B0%D0%B9%20%D0%B1%D0%B0%D1%82%D1%8B%D1%80%D0%B0%206%2F5%20%D0%AD%D1%82%D0%B0%D0%BB%D0%BE%D0%BD'
    },
    {
      id: 4,
      name: { ru: 'ТОО "Astana Service Automatic"', kz: 'ЖШС "Astana Service Automatic"', en: 'Astana Service Automatic LLP', ar: 'شركة أستانا سيرفيس أوتوماتيك ذ.م.م' },
      city: 'astana',
      cityName: { ru: 'Астана', kz: 'Астана', en: 'Astana', ar: 'أستانا' },
      address: { 
        ru: 'г. Астана, ул. Вячеславская 1, оф. 404', 
        kz: 'Астана қ., Вячеславская көшесі 1, 404 кеңсе', 
        en: 'Astana, Vyacheslavskaya str. 1, office 404', ar: 'أستانا، شارع فياتشيسلافسكايا 1، مكتب 404' 
      },
      phones: ['+7 771 505 2266'],
      website: 'https://panel.kz',
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/astana/search/%D0%90%D1%81%D1%82%D0%B0%D0%BD%D0%B0%2C%20%D0%92%D1%8F%D1%87%D0%B5%D1%81%D0%BB%D0%B0%D0%B2%D1%81%D0%BA%D0%B0%D1%8F%201'
    },
    {
      id: 5,
      name: { ru: 'ТОО "ON Development"', kz: 'ЖШС "ON Development"', en: 'ON Development LLP', ar: 'شركة أو إن ديفيلوبمنت ذ.م.م' },
      city: 'aktau',
      cityName: { ru: 'Актау', kz: 'Ақтау', en: 'Aktau', ar: 'أكتاو' },
      address: { 
        ru: 'г. Актау, ЖК Жанболат, мкр-н. 17, дом 21', 
        kz: 'Ақтау қ., Жанболат ТК, 17-ші ықшамауд., 21 үй', 
        en: 'Aktau, Zhanbolat RC, microdistrict 17, building 21', ar: 'أكتاو، مركز جانبولات التجاري، الحي السكني 17، مبنى 21' 
      },
      phones: ['+7 702 120 2626'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/aktau/search/%D0%90%D0%BA%D1%82%D0%B0%D1%83%2C%2017%20%D0%BC%D0%B8%D0%BA%D1%80%D0%BE%D1%80%D0%B0%D0%B9%D0%BE%D0%BD%2021'
    },
    {
      id: 6,
      name: { ru: 'ТОО "Строймаркет плюс"', kz: 'ЖШС "Строймаркет плюс"', en: 'Stroymarket Plus LLP', ar: 'شركة ستروي ماركت بلاس ذ.م.م' },
      city: 'kostanay',
      cityName: { ru: 'Костанай', kz: 'Қостанай', en: 'Kostanay', ar: 'قوستاناي' },
      address: { 
        ru: 'г. Костанай, ул. Жанибек Батыра, дом 12', 
        kz: 'Қостанай қ., Жәнібек Батыр көшесі, 12 үй', 
        en: 'Kostanay, Zhanibek Batyr str., 12', ar: 'قوستاناي، شارع جانيبيك باتير، 12' 
      },
      phones: ['+7 777 053 8800'],
      workHours: { ru: 'Пн-Сб: 9:00-18:00', kz: 'Дс-Сб: 9:00-18:00', en: 'Mon-Sat: 9:00-18:00', ar: 'الاثنين - السبت: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/kostanay/search/%D0%9A%D0%BE%D1%81%D1%82%D0%B0%D0%BD%D0%B0%D0%B9%2C%20%D0%96%D0%B0%D0%BD%D0%B8%D0%B1%D0%B5%D0%BA%20%D0%91%D0%B0%D1%82%D1%8B%D1%80%D0%B0%2012'
    },
    {
      id: 7,
      name: { ru: 'ТОО "Mega Ceramic"', kz: 'ЖШС "Mega Ceramic"', en: 'Mega Ceramic LLP', ar: 'شركة ميغا سيراميك ذ.م.م' },
      city: 'semey',
      cityName: { ru: 'Семей', kz: 'Семей', en: 'Semey', ar: 'سيمي' },
      address: { 
        ru: 'г. Семей, ул. Шыракбека Кабылбаева, 33', 
        kz: 'Семей қ., Шырақбек Қабылбаев көшесі, 33', 
        en: 'Semey, Shyrakbek Kabylbaev str., 33', ar: 'سيمي، شارع شيراكبيك كابيلبايف، 33' 
      },
      phones: ['+7 771 516 6252'],
      workHours: { ru: 'Пн-Сб: 9:00-18:00', kz: 'Дс-Сб: 9:00-18:00', en: 'Mon-Sat: 9:00-18:00', ar: 'الاثنين - السبت: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/semey/geo/70030076252002477/80.235520,50.439255'
    },
    {
      id: 8,
      name: { ru: 'ТОО "Казвинилстрой"', kz: 'ЖШС "Казвинилстрой"', en: 'Kazvinilstroy LLP', ar: 'شركة كازفينيلستروي ذ.م.م' },
      city: 'ust-kamenogorsk',
      cityName: { ru: 'Усть-Каменогорск', kz: 'Өскемен', en: 'Ust-Kamenogorsk', ar: 'أوست-كامينوغورسك' },
      address: { 
        ru: 'г. Усть-Каменогорск, ул. Самарское шоссе, дом 11, склад № 2', 
        kz: 'Өскемен қ., Самар тас жолы көшесі, 11 үй, № 2 қойма', 
        en: 'Ust-Kamenogorsk, Samarskoe highway, 11, warehouse 2', ar: 'أوست-كامينوغورسك، طريق ساماسكوي السريع، 11، مستودع 2' 
      },
      phones: ['+7 701 982 5871'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/ust_kamenogorsk/search/%D0%A3%D1%81%D1%82%D1%8C-%D0%9A%D0%B0%D0%BC%D0%B5%D0%BD%D0%BE%D0%B3%D0%BE%D1%80%D1%81%D0%BA%2C%20%D0%A1%D0%B0%D0%BC%D0%B0%D1%80%D1%81%D0%BA%D0%BE%D0%B5%20%D1%88%D0%BE%D1%81%D1%81%D0%B5%2011'
    },
    {
      id: 9,
      name: { ru: 'ТОО "Аквилон Трейд"', kz: 'ЖШС "Аквилон Трейд"', en: 'Akvilon Trade LLP', ar: 'شركة أكفيلون تريد ذ.م.م' },
      city: 'pavlodar',
      cityName: { ru: 'Павлодар', kz: 'Павлодар', en: 'Pavlodar', ar: 'بافلودار' },
      address: { 
        ru: 'г. Павлодар, ул. Торговая, дом 5/5', 
        kz: 'Павлодар қ., Сауда көшесі, 5/5 үй', 
        en: 'Pavlodar, Torgovaya str., 5/5', ar: 'بافلودار، شارع تورغوفايا، 5/5' 
      },
      phones: ['+7 705 707 8008'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/pavlodar/search/%D0%9F%D0%B0%D0%B2%D0%BB%D0%BE%D0%B4%D0%B0%D1%80%2C%20%D0%A2%D0%BE%D1%80%D0%B3%D0%BE%D0%B2%D0%B0%D1%8F%205%2F5'
    },
    {
      id: 10,
      name: { ru: 'ТОО "Аквилон Трейд"', kz: 'ЖШС "Аквилон Трейд"', en: 'Akvilon Trade LLP', ar: 'شركة أكفيلون تريد ذ.م.م' },
      city: 'ekibastuz',
      cityName: { ru: 'Экибастуз', kz: 'Екібастұз', en: 'Ekibastuz', ar: 'إيكيباستوز' },
      address: { 
        ru: 'г. Экибастуз, ул. Железнодорожная, дом 27', 
        kz: 'Екібастұз қ., Темір жол көшесі, 27 үй', 
        en: 'Ekibastuz, Zheleznodorozhnaya str., 27', ar: 'إيكيباستوز، شارع زيليزنودوروجنايا، 27' 
      },
      phones: ['+7 705 707 8008'],
      workHours: { ru: 'Пн-Пт: 9:00-18:00', kz: 'Дс-Жм: 9:00-18:00', en: 'Mon-Fri: 9:00-18:00', ar: 'الاثنين - الجمعة: 9:00 - 18:00' },
      mapLink: 'https://2gis.kz/ekibastuz/search/%D0%AD%D0%BA%D0%B8%D0%B1%D0%B0%D1%81%D1%82%D1%83%D0%B7%2C%20%D0%96%D0%B5%D0%BB%D0%B5%D0%B7%D0%BD%D0%BE%D0%B4%D0%BE%D1%80%D0%BE%D0%B6%D0%BD%D0%B0%D1%8F%2027'
    }
  ];

  // Helper function to get WhatsApp link
  const getWhatsAppLink = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '').replace('+', '');
    return `https://wa.me/${cleanPhone}`;
  };

  // Helper function to get clean phone number for tel: link
  const getPhoneLink = (phone) => {
    return phone.replace(/[\s\-\(\)]/g, '');
  };

  const _tt = content[language] || content.en || content.ru;
  const cities = [
    { value: 'all', label: _tt.dealers.all },
    { value: 'almaty', label: { ru: 'Алматы', kz: 'Алматы', en: 'Almaty', ar: 'ألماتي' }[language] || 'Almaty' },
    { value: 'astana', label: { ru: 'Астана', kz: 'Астана', en: 'Astana', ar: 'أستانا' }[language] || 'Astana' },
    { value: 'aktau', label: { ru: 'Актау', kz: 'Ақтау', en: 'Aktau', ar: 'أكتاو' }[language] || 'Aktau' },
    { value: 'kostanay', label: { ru: 'Костанай', kz: 'Қостанай', en: 'Kostanay', ar: 'كوستاناي' }[language] || 'Kostanay' },
    { value: 'semey', label: { ru: 'Семей', kz: 'Семей', en: 'Semey', ar: 'سيمي' }[language] || 'Semey' },
    { value: 'ust-kamenogorsk', label: { ru: 'Усть-Каменогорск', kz: 'Өскемен', en: 'Ust-Kamenogorsk', ar: 'أوست-كامينوغورسك' }[language] || 'Ust-Kamenogorsk' },
    { value: 'pavlodar', label: { ru: 'Павлодар', kz: 'Павлодар', en: 'Pavlodar', ar: 'بافلودار' }[language] || 'Pavlodar' },
    { value: 'ekibastuz', label: { ru: 'Экибастуз', kz: 'Екібастұз', en: 'Ekibastuz', ar: 'إكيباستوز' }[language] || 'Ekibastuz' }
  ];

  const t = _tt;

  const filteredDealers = selectedCity === 'all' 
    ? dealers 
    : dealers.filter(d => d.city === selectedCity);

  const handleWhatsApp = () => {
    window.open('https://wa.me/77717004430', '_blank');
  };

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="contact-page">
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      {/* Header */}
      <section className="py-8 pb-16 bg-[#061d36] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="contact-title">{t.header.title}</h1>
          <p className="text-lg text-blue-200">{t.header.subtitle}</p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Left Column - Maps */}
              <div className="space-y-6">
                {/* Production Map Card - Astana */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid="map-card-production">
                  <div className="p-4 border-b bg-[#061d36]">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      {language === 'ru' ? 'Производство (Астана)' : 
                       language === 'kz' ? 'Өндіріс (Астана)' : 
                       'Production (Astana)'}
                    </h3>
                  </div>
                  <a href="https://2gis.kz/astana/firm/70000001090667528" target="_blank" rel="noopener noreferrer" className="block relative" style={{ height: '180px' }}>
                    <iframe
                      src="https://yandex.ru/map-widget/v1/?ll=71.510051%2C51.181686&z=17&pt=71.510051%2C51.181686%2Cpm2rdm&l=map"
                      className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                      title="Map - Production Astana"
                      loading="lazy"
                    />
                  </a>
                  <div className="p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#061d36] text-sm">ТОО "Гипсовинил"</p>
                      <p className="text-gray-500 text-xs truncate">
                        {language === 'ru' ? 'СЭЗ Астана' : 
                         language === 'kz' ? 'АЭА Астана' : 
                         'SEZ Astana'}
                      </p>
                    </div>
                    <a
                      href="https://2gis.kz/astana/firm/70000001090667528"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#061d36] hover:bg-[#0d2a47] text-white rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      2GIS
                    </a>
                  </div>
                </div>

                {/* Showroom Map Card - Almaty */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid="map-card-showroom">
                  <div className="p-4 border-b bg-[#8B4444]">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      {language === 'ru' ? 'Шоурум (Алматы)' : 
                       language === 'kz' ? 'Шоурум (Алматы)' : 
                       'Showroom (Almaty)'}
                    </h3>
                  </div>
                  <a href="https://2gis.kz/almaty/geo/70000001103677905" target="_blank" rel="noopener noreferrer" className="block relative" style={{ height: '180px' }}>
                    <iframe
                      src="https://yandex.ru/map-widget/v1/?ll=76.900222%2C43.216557&z=16&pt=76.900222%2C43.216557%2Cpm2rdm&l=map"
                      className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                      title="Map - Showroom Almaty"
                      loading="lazy"
                    />
                  </a>
                  <div className="p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#061d36] text-sm">
                        {language === 'ru' ? 'Шоурум Гипсовинил' : 
                         language === 'kz' ? 'Гипсовинил шоурумы' : 
                         'GIPSOVINIL Showroom'}
                      </p>
                      <p className="text-gray-500 text-xs truncate">
                        {language === 'ru' ? 'г. Алматы' : 
                         language === 'kz' ? 'Алматы қ.' : 
                         'Almaty'}
                      </p>
                    </div>
                    <a
                      href="https://2gis.kz/almaty/geo/70000001103677905"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      2GIS
                    </a>
                  </div>
                </div>
              </div>

              {/* Right Column - WhatsApp + Contact Information */}
              <div className="space-y-6">
                {/* WhatsApp Card */}
                <a
                  href="https://wa.me/77717004430"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[#25D366] rounded-2xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                  data-testid="whatsapp-card"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <WhatsAppIcon className="w-6 h-6" />
                    <div>
                      <h3 className="text-xl font-bold">{t.whatsapp.title}</h3>
                      <p className="text-green-100 text-sm">{t.whatsapp.subtitle}</p>
                    </div>
                  </div>
                  <span
                    className="block w-full bg-[#20BD5A] hover:bg-[#1DA851] text-white rounded-lg py-3 font-medium transition-all mt-2 text-center"
                  >
                    {t.whatsapp.button}
                  </span>
                </a>

                {/* Contact Information */}
                <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="contact-info-card">
                  <h3 className="text-xl font-bold text-[#061d36] mb-6">{t.contactInfo.title}</h3>
                  
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">
                          {language === 'ru' ? 'Отдел продаж' : 
                           language === 'kz' ? 'Сату бөлімі' : 
                           'Sales Department'}
                        </p>
                        <a href={`tel:${t.contactInfo.phone2.raw}`} className="text-[#061d36] font-medium hover:text-blue-600 transition-colors block">
                          {t.contactInfo.phone2.value}
                        </a>
                        {t.contactInfo.phone3 && (
                          <a href={`tel:${t.contactInfo.phone3.raw}`} className="text-[#061d36] font-medium hover:text-blue-600 transition-colors block mt-1" data-testid="contact-phone3">
                            {t.contactInfo.phone3.value}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">
                          {language === 'ru' ? 'Склад' : 
                           language === 'kz' ? 'Қойма' : 
                           'Warehouse'}
                        </p>
                        <a href="tel:+77717003669" className="text-[#061d36] font-medium hover:text-blue-600 transition-colors block">
                          +7 771 700 36 69
                        </a>
                        <a href="tel:+77775355948" className="text-[#061d36] font-medium hover:text-blue-600 transition-colors block mt-1">
                          +7 777 535 59 48
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{t.contactInfo.email.label}</p>
                        <a href={`mailto:${t.contactInfo.email.value}`} className="text-[#061d36] font-medium hover:text-[#061d36]">
                          {t.contactInfo.email.value}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#8b4444]" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{t.contactInfo.ceoEmail.label}</p>
                        <a href={`mailto:${t.contactInfo.ceoEmail.value}`} className="text-[#061d36] font-medium hover:text-[#061d36]">
                          {t.contactInfo.ceoEmail.value}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{t.contactInfo.address.label}</p>
                        <p className="text-[#061d36] font-medium">{t.contactInfo.address.value}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{t.contactInfo.hours.label}</p>
                        <p className="text-[#061d36] font-medium">{t.contactInfo.hours.value}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Route Scheme to Production */}
      <RouteScheme />

      {/* Dealers Section */}
      <section className={`py-8 bg-[#F5F5F0] relative ${isEditMode ? 'ring-2 ring-blue-400 ring-inset' : ''}`} data-testid="dealers-section">
        {isEditMode && (
          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded z-10">
            Секция: Дилеры ({dealers.length} шт.)
          </div>
        )}
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#061d36] mb-3">{t.dealers.title}</h2>
            <p className="text-[#061d36]/70">{t.dealers.subtitle}</p>
          </div>

          {/* City Filter */}
          <div className="mb-8 flex flex-wrap justify-center gap-2">
            {cities.map((city) => (
              <button
                key={city.value}
                onClick={() => setSelectedCity(city.value)}
                data-testid={`city-filter-${city.value}`}
                className={`px-5 py-2.5 rounded-xl font-medium transition-all text-sm ${
                  selectedCity === city.value
                    ? 'bg-[#061d36] text-white shadow-lg'
                    : 'bg-white text-[#061d36] hover:bg-gray-100'
                }`}
              >
                {city.label}
              </button>
            ))}
          </div>

          {/* Dealers Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {filteredDealers.map((dealer) => (
              <div
                key={dealer.id}
                data-testid={`dealer-card-${dealer.id}`}
                className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-sm text-[#8B4444] font-medium">{dealer.cityName[language]}</span>
                    <h3 className="text-lg font-bold text-[#061d36]">{dealer.name[language]}</h3>
                  </div>
                  <a
                    href={dealer.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[#061d36] hover:text-[#061d36]/70 transition-colors bg-gray-100 px-2 py-1 rounded-lg"
                  >
                    <ExternalLink className="w-3 h-3" />
                    2GIS
                  </a>
                </div>

                <div className="space-y-4 text-sm mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-[#061d36] pt-1">{dealer.address[language]}</span>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex flex-col pt-1">
                      {dealer.phones.map((phone, idx) => (
                        <a key={idx} href={`tel:${getPhoneLink(phone)}`} className="text-[#061d36] font-medium hover:text-blue-600 transition-colors">
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-[#061d36]">{dealer.workHours[language]}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <a
                    href={getWhatsAppLink(dealer.phones[0])}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 px-4 bg-[#25D366] text-white text-center rounded-xl font-medium hover:bg-[#20BD5A] transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {t.dealers.contactDealer}
                  </a>
                  <a
                    href={dealer.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2.5 px-4 border-2 border-[#061d36] text-[#061d36] rounded-xl font-medium hover:bg-[#061d36] hover:text-white transition-colors text-sm"
                  >
                    {t.dealers.showOnMap}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
